import { BusinessBusiness, TagExplanation } from "../types";
import { extractAndVerifyHiringEvidence, calculateEvidenceGroundedHiringScore } from "./hiringSignalEngine";

export interface CategorizedSignals {
  workOpportunities: string[];
  growthSignals: string[];
  serviceOpportunities: string[];
  contactSignals: string[];
  warnings: string[];
  aiAutomation: string[];
}

export interface ScoreBreakdownItem {
  label: string;
  points: number;
}

/**
 * Generates custom structured metadata with plain-English rationales for every signal / opportunity tag.
 */
export function getTagExplanation(tagName: string, lead: BusinessBusiness, distanceInMiles?: number | null): TagExplanation {
  const nameLower = lead.name.toLowerCase();
  const catLower = (lead.category || "").toLowerCase();
  const audit = lead.websiteAudit;

  let category: TagExplanation['category'] = 'Work Opportunities';
  let reason = `Active signal related to ${tagName}.`;
  let source = "Google Maps Listing";
  let confidence: TagExplanation['confidence'] = 'Basic';

  switch (tagName) {
    // --- 1. Work Opportunities ---
    case "Currently Hiring":
      category = "Work Opportunities";
      reason = `${lead.name} operates in the ${lead.category || "service"} sector with verified hiring activity. ${lead.hiringPageUrl ? `Active careers portal at ${lead.hiringPageUrl}.` : `Verified job ads and active recruitment indicators on file.`}`;
      source = lead.hiringPageUrl ? "Official Careers Portal" : "Job Board & Business Registry";
      confidence = lead.hiringPageUrl ? "Full" : "Improved";
      break;
    case "Hiring Multiple Roles":
      category = "Work Opportunities";
      reason = `${lead.name} exhibits multi-role staffing demand across operational departments based on current listings.`;
      source = lead.hiringPageUrl ? "Official Careers Portal" : "Public Job Ads & Registry";
      confidence = "Full";
      break;
    case "New Business":
      category = "Work Opportunities";
      reason = `${lead.name} is a newly registered local entity (${lead.reviewsCount || 1} review on file), indicating initial team building and staffing needs.`;
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Expanding Location":
      category = "Work Opportunities";
      reason = `${lead.name} has location expansion indicators registered at ${lead.address}, suggesting branch growth and new hiring positions.`;
      source = "Google Business Information";
      confidence = "Improved";
      break;
    case "High Hiring Signal":
      category = "Work Opportunities";
      reason = `Analysis confirmed strong hiring evidence for ${lead.name} in the ${lead.category || "local service"} category (${lead.reviewsCount || 0} reviews, ${lead.rating ? lead.rating.toFixed(1) : '4.0'}★).`;
      source = lead.hiringPageUrl ? "Careers Page & Business Audit" : "Google Business & Web Scan";
      confidence = "Full";
      break;
    case "Seasonal Hiring Likely":
      category = "Work Opportunities";
      reason = `${lead.name} operates in ${lead.category || "a seasonal industry"} which undergoes cyclic recruitment drives.`;
      source = "Industry Sourcing Data";
      confidence = "Improved";
      break;

    // --- 2. Growth Signals ---
    case "Strong Reviews":
      category = "Growth Signals";
      reason = "A solid baseline of over 30 reviews with a high overall satisfaction rating (>= 4.5) indicates premium local services and high trust.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Weak Reviews":
      category = "Growth Signals";
      reason = "Average feedback score is under 4.0, indicating a clear, urgent opportunity to improve reputation using review management frameworks.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "High Local Demand":
      category = "Growth Signals";
      reason = "Exceptional user interaction levels, with more than 150 local reviews or a near-perfect rating threshold of >= 4.8.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "High Competition Area":
      category = "Growth Signals";
      reason = "Located in a highly populated competitive metropolitan market where continuous lead optimization provides massive ROI.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Under-Marketed Business":
      category = "Growth Signals";
      reason = "The listing displays great organic trust scores (>= 4.3) but is constrained by low review counts (< 15) and lacks a dedicated lead capture website.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Good Outreach Target":
      category = "Growth Signals";
      reason = "Features responsive ratings (>= 4.0) along with a valid direct contact phone number on the local listing.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Fast Response Likely":
      category = "Growth Signals";
      reason = "Classified as an on-demand trade segment (e.g., towing, locksmithing) where quick turnaround translates directly to business survival.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Decision Maker Likely Local":
      category = "Growth Signals";
      reason = "No corporate prefixes/suffixes and a single physical location indicate local management answers the phone directly.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "High Competition":
    case "high_competition":
    case "Well-Known Employer":
    case "Large Recognized Employer":
    case "Highly Visible Brand":
    case "Major Market Presence":
      category = "Growth Signals";
      reason = "Many people are likely to recognize and contact this business, so competition may be higher.";
      source = "Brand Recognition & Traffic Analysis";
      confidence = "Full";
      break;

    // --- Plain Visual Tag Explanations ---
    case "Highly Reviewed":
      category = "Growth Signals";
      reason = `With over 100 customer reviews (${lead.reviewsCount || 100} on file), this business has a highly established public footprint.`;
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Strong Local Reputation":
      category = "Growth Signals";
      reason = `Features an exceptional local user rating of ${lead.rating ? lead.rating.toFixed(1) : '4.5'} stars, proving high customer satisfaction.`;
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "New or Low-Review Listing":
      category = "Growth Signals";
      reason = "A fresh or boutique local listing with a small review sample, ideal for reputation building.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Website Available":
      category = "Service Opportunities";
      reason = "Has an active official website listing to easily check services and credibility.";
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Contact Info Available":
      category = "Contact Signals";
      reason = "Active business phone or direct team coordinates are verified and on-file.";
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Open Now":
      category = "Growth Signals";
      reason = "Operating hours and listings confirm this business is open for immediate service.";
      source = "Google Maps Listing";
      confidence = "Improved";
      break;
    case "Nearby":
      category = "Growth Signals";
      reason = `Located in close geographic proximity (${distanceInMiles ? distanceInMiles.toFixed(1) : '1.5'} miles), saving valuable travel or commute time.`;
      source = "Geographic Analysis";
      confidence = "Full";
      break;
    case "Easy to Contact":
      category = "Contact Signals";
      reason = "Direct telephone number or active contact path is available.";
      source = "System Analysis";
      confidence = "Full";
      break;
    case "Application Link Available":
      category = "Work Opportunities";
      reason = "Direct digital scheduling or online job application forms are active.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Clear Pay Listed":
      category = "Work Opportunities";
      reason = "The pay range and rate details are clearly stated upfront for transparency.";
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Likely Hiring Category":
      category = "Work Opportunities";
      reason = `Operates in a high-demand sector (${lead.category || "service"}) with consistent local hiring and staffing needs.`;
      source = "System Analysis";
      confidence = "Improved";
      break;
    case "Careers Page Found":
      category = "Work Opportunities";
      reason = "Active recruiting page or careers channel identified on the business website.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Multiple Locations":
      category = "Growth Signals";
      reason = "Part of a growing multi-location brand or business group.";
      source = "Google Maps Listing";
      confidence = "Improved";
      break;
    case "Long Business Hours":
      category = "Growth Signals";
      reason = "Operates extended schedules, indicating high operational volume and shift needs.";
      source = "Google Maps Listing";
      confidence = "Improved";
      break;
    case "No Website Found":
      category = "Service Opportunities";
      reason = "Completely lacks an active website, presenting a massive redesign or launch opportunity.";
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Website Needs Review":
      category = "Service Opportunities";
      reason = "The website lacks key responsive, security, or contact elements.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Booking Missing":
      category = "Service Opportunities";
      reason = "No online booking, reservation, or scheduler found on their official website.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Contact Form Missing":
      category = "Service Opportunities";
      reason = "Lacks a digital lead capture or contact form on their website.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "High Demand Category":
      category = "Growth Signals";
      reason = "High consumer search volume and category traffic in this geographical region.";
      source = "System Analysis";
      confidence = "Improved";
      break;
    case "Competitor Pressure":
      category = "Growth Signals";
      reason = "Operates in a highly saturated local hub with active digital competitors.";
      source = "Geographic Analysis";
      confidence = "Improved";
      break;
    case "Sponsored":
      category = "Growth Signals";
      reason = "This is a premium promoted campaign with enhanced priority search placement.";
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Advertises Everywhere":
      category = "Growth Signals";
      reason = "The business maintains broad ad campaigns across multiple metropolitan areas.";
      source = "System Analysis";
      confidence = "Improved";
      break;
    case "Sponsored Near You":
      category = "Growth Signals";
      reason = "A local sponsor ad highlighted specifically for your target neighborhood range.";
      source = "Geographic Analysis";
      confidence = "Full";
      break;
    case "Promoted Job":
      category = "Work Opportunities";
      reason = "An active promoted job listing placed with higher search prominence.";
      source = "System Analysis";
      confidence = "Full";
      break;

    // --- New Google Maps Listing Explanations ---
    case "Elite Local Reputation":
      category = "Growth Signals";
      reason = `This business has an outstanding, tier-one rating average of ${lead.rating ? lead.rating.toFixed(1) : '4.7'} stars backed by a robust sample size of ${lead.reviewsCount || 50} reviews, establishing them as an elite local market authority.`;
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Untapped Growth Potential":
      category = "Growth Signals";
      reason = `Boasts an exceptional average user rating (${lead.rating ? lead.rating.toFixed(1) : '4.5'}+ stars) but is held back by low public review volume (${lead.reviewsCount || 0} reviews), presenting a prime local reputation acceleration opportunity.`;
      source = "Google Maps Listing";
      confidence = "Improved";
      break;
    case "Low Feedback Footprint":
      category = "Growth Signals";
      reason = `This business currently has extremely minimal consumer review presence (${lead.reviewsCount || 0} reviews), leaving them vulnerable to digital-savvy competitors.`;
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "High Consumer Engagement":
      category = "Growth Signals";
      reason = `With a massive base of ${lead.reviewsCount || 100} reviews, this business actively handles high local consumer volume and maintains an active public footprint.`;
      source = "Google Maps Listing";
      confidence = "Improved";
      break;
    case "Pristine 5-Star Standing":
      category = "Growth Signals";
      reason = `Features a perfect 5.0 out of 5.0 customer satisfaction score over ${lead.reviewsCount || 4} client submissions—an incredibly rare and prestigious operational benchmark.`;
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Reputation Repair Prospect":
      category = "Growth Signals";
      reason = `Average rating of ${lead.rating ? lead.rating.toFixed(1) : '3.8'} indicates active service gaps or customer friction, making this a highly urgent target for local review restoration campaigns.`;
      source = "Google Maps Listing";
      confidence = "Full";
      break;
    case "Boutique Scale Operations":
      category = "Growth Signals";
      reason = `A non-corporate entity with standard boutique scale reviews profile (${lead.reviewsCount || 0} reviews), allowing for direct ownership contact and rapid decision timelines.`;
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Unclaimed Listing Opportunity":
      category = "Growth Signals";
      reason = "Completely unmanaged or unclaimed directory profile with missing communication channels, offering an immediate value-add target for local agency services.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;

    // --- New Geographic Analysis Explanations ---
    case "Immediate Walk-In Range":
      category = "Growth Signals";
      reason = `Located in hyper-local immediate range, currently calculated at a convenient ${distanceInMiles ? distanceInMiles.toFixed(2) : '1.2'} miles from the target search hub, streamlining fast onsite visits.`;
      source = "Geographic Analysis";
      confidence = "Full";
      break;
    case "Sub-5 Mile Commute Proximity":
      category = "Growth Signals";
      reason = `Located within a highly convenient ${distanceInMiles ? distanceInMiles.toFixed(1) : '3.5'} miles, ensuring rapid commutes and highly localized on-demand service delivery.`;
      source = "Geographic Analysis";
      confidence = "Full";
      break;
    case "Regional Hub Coverage":
      category = "Growth Signals";
      reason = `Located in the wider regional metropolitan circle (${distanceInMiles ? distanceInMiles.toFixed(1) : '12.0'} miles away), accessing an extensive territorial customer base.`;
      source = "Geographic Analysis";
      confidence = "Improved";
      break;
    case "High Walkability Score":
      category = "Growth Signals";
      reason = "Situated in a highly active shopping district or commercial avenue, capturing high volumes of visual foot traffic and walk-in sales.";
      source = "Geographic Analysis";
      confidence = "Improved";
      break;
    case "Transit-Oriented Corridor":
      category = "Growth Signals";
      reason = "Positioned within a high-accessibility corridor near stations or metropolitan transit terminals, offering unmatched employee and client commute convenience.";
      source = "Geographic Analysis";
      confidence = "Improved";
      break;
    case "Ground Floor Access":
      category = "Growth Signals";
      reason = "Located on the ground level, storefront unit, or main lobby entry, ensuring frictionless physical accessibility and convenient freight/customer access.";
      source = "Geographic Analysis";
      confidence = "Improved";
      break;
    case "Suburban Community Sanctuary":
      category = "Growth Signals";
      reason = "Nestled in a highly stable residential suburb with strong local customer density, ample on-premise parking, and lower operational overhead.";
      source = "Geographic Analysis";
      confidence = "Basic";
      break;
    case "Metro Commercial Density Advantage":
      category = "Growth Signals";
      reason = "Located in a high-density, multi-billion dollar metropolitan business hub, unlocking continuous organic traffic and local enterprise partnerships.";
      source = "Geographic Analysis";
      confidence = "Improved";
      break;

    // --- 3. Service Opportunities ---
    case "Website Not Reviewed Yet":
      category = "Service Opportunities";
      reason = "This business has a website, but BizNearYou has not run its website review yet. Opening the website manually does not count as a website review.";
      source = "Google Maps listing provided a website URL.";
      confidence = "Basic";
      break;
    case "Website Review Outdated":
      category = "Service Opportunities";
      reason = "The website review is old and should be refreshed to recapture the latest technical status.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Website Review Failed":
      category = "Service Opportunities";
      reason = "The custom scraper or network crawler was unable to load home layouts, possibly because of firewall protection rules or DNS server failures.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Needs Website Cleanup":
      category = "Service Opportunities";
      const issues: string[] = [];
      if (audit) {
        if (!audit.contactFormFound) issues.push("missing contact form");
        if (!audit.bookingButtonFound) issues.push("no booking button");
        if (!audit.mobileFriendly) issues.push("not mobile friendly");
        if (!audit.https) issues.push("not HTTPS");
        if (audit.outdatedDetails) issues.push("outdated details");
        if (!audit.seoTitleExists) issues.push("missing SEO metadata");
      } else {
        issues.push("unresolvable web layouts");
      }
      reason = `Website review found critical issues: ${issues.join(", ")}.`;
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Needs Online Booking":
      category = "Service Opportunities";
      reason = "Scanned home directories and templates but found no online calendar widget or reservation path to schedule services instantly.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Needs Better Follow-Up":
      category = "Service Opportunities";
      reason = "Website lacks a clean, interactive customer entry form to capture names and emails, leading to lost digital traffic.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Needs Social Media Help":
      category = "Service Opportunities";
      reason = "No active links or profile icons for major external channels (Facebook, Instagram, LinkedIn) were detected on their home files.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Needs Review Help":
      category = "Service Opportunities";
      reason = "Local listing trust ratings are below average (< 4.2), meaning automated text/email feedback links will yield massive reputation gains.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Needs Local Ads":
      category = "Service Opportunities";
      reason = "Local rating levels are excellent, but overall brand exposure is bottlenecked by low review counts or zero web page footprint.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Needs Better Photos":
      category = "Service Opportunities";
      reason = "Stale website content or complete lack of visual gallery links implies outdated portfolio showcases.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Needs Customer Support Help":
      category = "Service Opportunities";
      reason = "Operates a functional consumer website but fails to specify a contact phone number in Google Directory outputs.";
      source = "Google Maps Listing / Website Review";
      confidence = "Basic";
      break;

    // --- New System Analysis Explanations ---
    case "Verified Contact Integrity":
      category = "Contact Signals";
      reason = "System successfully identified, scraped, and cross-referenced direct company contact pathways, ensuring highly deliverable sales campaigns.";
      source = "Contact Search / Crawler";
      confidence = "Full";
      break;
    case "Verified Decision-Maker Found":
      category = "Contact Signals";
      reason = `Found a direct executive contact pathway (CEO, Owner, Founder, or Manager) for ${lead.contacts[0]?.name || "ownership"}, bypassing generic info@ mailboxes completely.`;
      source = "Contact Search / Crawler";
      confidence = "Full";
      break;
    case "Direct Voice Channel":
      category = "Contact Signals";
      reason = `Verified active telephone pathway (${lead.phone || 'on-file'}), facilitating immediate live voice calls and high-priority customer support verification.`;
      source = "System Analysis";
      confidence = "Full";
      break;
    case "Crawler Audit: Website Verified":
      category = "Service Opportunities";
      reason = "Web crawler successfully handshaked, crawled, and compiled technology layout footprints, providing a perfect baseline of digital opportunities.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Secure SSL Encryption Found":
      category = "Service Opportunities";
      reason = "Website fully enforces HTTPS protocols with verified certificates, protecting customer input and maintaining standard search indexing advantages.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Mobile Responsive Layout":
      category = "Service Opportunities";
      reason = "The homepage viewport rules and media queries are optimized for modern smartphones, retaining high volumes of mobile searchers.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Lead-Capture Form Active":
      category = "Service Opportunities";
      reason = "Contains an active HTML contact form to dynamically collect names, emails, and phone numbers from organic webpage traffic.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Automated Scheduling Online":
      category = "Service Opportunities";
      reason = "An interactive, online booking widget or reservation button is present, allowing web traffic to secure slots on autopilot.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "HTML SEO Title Present":
      category = "Service Opportunities";
      reason = "Active, keyword-relevant HTML page title tag detected, supporting local indexing and click-through rates on search platforms.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Missing Digital Form Risk":
      category = "Service Opportunities";
      reason = "Lacks any online input form or quote request field, resulting in substantial digital visitor drop-offs since users cannot reach out instantly.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Booking System Gap":
      category = "Service Opportunities";
      reason = "No online reservation calendar or real-time booking button found, forcing clients to make tedious telephone coordination tags.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "SSL Security Risk":
      category = "Service Opportunities";
      reason = "The website does not enforce HTTPS, displaying 'Not Secure' browser warnings that scare away visitors and penalize search rankings.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Mobile-Friendliness Gap":
      category = "Service Opportunities";
      reason = "Web layout is not optimized for handheld screens, stretching text and misaligning buttons, leading to high mobile visitor bounce rates.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "Outdated Content Exposure":
      category = "Service Opportunities";
      reason = "Crawler flagged older templates, obsolete year tags, or neglected text paragraphs, suggesting the business website is currently unmaintained.";
      source = "Website Review";
      confidence = "Full";
      break;
    case "SEO Optimization Opportunity":
      category = "Service Opportunities";
      reason = "Lacks structured page titles or SEO descriptors, resulting in minimal search presence and invisible local keyword search rankings.";
      source = "Website Review";
      confidence = "Full";
      break;

    // --- 4. Contact Signals ---
    case "Owner/manager contact found":
      category = "Contact Signals";
      reason = "Direct professional listings parsed names and positions matching active ownership or company executives.";
      source = "Contact Search";
      confidence = "Full";
      break;
    case "Direct contact found":
      category = "Contact Signals";
      reason = "Extracted verified email identities or phone paths belonging directly to internal organizational roles.";
      source = "Contact Search";
      confidence = "Full";
      break;
    case "Needs Contact Review":
      category = "Contact Signals";
      reason = "Retrieved records have variable formatting, requiring manual verification before launching outreach.";
      source = "Contact Search";
      confidence = "Full";
      break;
    case "Ready for Outreach":
      category = "Contact Signals";
      reason = "A verified communication route exists, enabling frictionless sales outreach campaigns.";
      source = "Contact Search";
      confidence = "Full";
      break;

    // --- 5. Warnings ---
    case "Possible Franchise":
      category = "Warnings";
      reason = "The listing name aligns with a known national brand chain, suggesting centralized corporate processes.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Possible Duplicate":
      category = "Warnings";
      reason = "Identical contact details are shared across another active business entry in the same territory.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Low Confidence Data":
      category = "Warnings";
      reason = "Severe absence of ratings, website link, and tel fields, indicating low confidence online presence details.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Contact Info Needs Review":
      category = "Warnings";
      reason = "Direct contact results are marked with low confidence scores, suggesting names or email patterns might be stale.";
      source = "Contact Search";
      confidence = "Full";
      break;
    case "Website Check Failed":
      category = "Warnings";
      reason = "Vite backend could not establish a solid HTTP connection handshake or fetch index content from the website.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Contact Search Failed":
      category = "Warnings";
      reason = "Contact crawler script encountered search directory blocks or unresolvable timeout parameters.";
      source = "Contact Search";
      confidence = "Full";
      break;
    case "Possibly Closed":
      category = "Warnings";
      reason = "Poor review ratings average (< 2.5) or marked inactive, hinting the business location may have shut down.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;

    // --- 6. AI & Automation ---
    case "Known AI Usage":
      category = "AI & Automation Opportunities";
      reason = "Profile metadata, titles, or active keywords contain references to automated agents, smart chatbots, or AI widgets.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Automation Opportunity": {
      category = "AI & Automation Opportunities";
      const causes: string[] = [];
      
      if (lead.website) {
        if (audit) {
          if (!audit.bookingButtonFound) {
            causes.push("the website is missing an automated scheduling option (bookings must be made manually)");
          }
          if (!audit.contactFormFound) {
            causes.push("the website does not feature a digital contact form (increasing lead drop-offs)");
          }
        } else {
          causes.push("the website has not been analyzed yet to see if it lacks automated booking or booking forms");
        }
      }
      
      if (lead.rating !== undefined && lead.rating > 0 && lead.rating < 4.5) {
        causes.push(`its rating is below elite threshold (${lead.rating} stars), making manual review replies slow`);
      }
      
      const isHighUrgency = ["locksmith", "towing", "roadside", "plumbing", "plumber", "hvac", "electrician", "dentist", "emergency", "taxi", "moving", "roofing"].some(r => catLower.includes(r));
      if (lead.phone && isHighUrgency) {
        causes.push(`its category ("${lead.category || "service"}") is high urgency, risking lost revenue from missed calls`);
      }
      
      reason = causes.length > 0
        ? `This business presents an automation opportunity because: ${causes.join("; ")}.`
        : "Business suffers from gaps like missing booking options, lack of contact channels, or low reviews, which can be improved with automated tools.";
      source = "System Analysis";
      confidence = "Improved";
      break;
    }
    case "Manual Booking Likely":
      category = "AI & Automation Opportunities";
      reason = "Website possesses active service offerings but handles bookings manually via phone tag rather than automatic scheduler tools.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Website Chat Opportunity":
      category = "AI & Automation Opportunities";
      reason = "Lacks any automated home widget or live greeting chat, leaving initial customer questions unanswered.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "Review Reply Automation Opportunity":
      category = "AI & Automation Opportunities";
      reason = "Benefit from an automated responder to manage low review trends or high incoming customer volumes.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Missed Call Follow-Up Opportunity":
      category = "AI & Automation Opportunities";
      reason = "High urgency services context requires phone follow-up SMS automation to instantly secure opportunities.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Quote Request Automation Opportunity":
      category = "AI & Automation Opportunities";
      reason = "Basic contact form detected, presenting an excellent opportunity to deploy a smart instant estimator.";
      source = "Website Review";
      confidence = "Improved";
      break;
    case "No AI Signal Found": {
      category = "AI & Automation Opportunities";
      const reasonsNoSignal: string[] = [];
      if (!lead.website) {
        reasonsNoSignal.push("it lacks a business website link to inspect for conversational gaps");
      } else if (audit && audit.bookingButtonFound && audit.contactFormFound) {
        reasonsNoSignal.push("its website already has an operational booking interface and digital contact form");
      }
      if (lead.rating !== undefined && lead.rating >= 4.5) {
        reasonsNoSignal.push(`its Google rating is already elite at ${lead.rating} stars`);
      }
      const isUrgent = ["locksmith", "towing", "roadside", "plumbing", "plumber", "hvac", "electrician", "dentist", "emergency", "taxi", "moving", "roofing"].some(r => catLower.includes(r));
      if (!isUrgent) {
        reasonsNoSignal.push(`its focus ("${lead.category || "service"}") does not require emergency-level instantaneous dispatching`);
      }

      reason = reasonsNoSignal.length > 0 
        ? `No automation gaps were identified because ${reasonsNoSignal.join(", and ")}.`
        : "The listing currently displays no automated gaps, negative reviews, or urgent real-time response requirements.";
      source = "System Analysis";
      confidence = "Basic";
      break;
    }
    // --- 7. Location Appeal & Age Requirement Signals ---
    case "Walkable Area":
      category = "Growth Signals";
      reason = "Located in a highly walkable commercial district or central avenue, making commuting on foot extremely convenient.";
      source = "Geographic Analysis";
      confidence = "Basic";
      break;
    case "Public Transit Access":
      category = "Growth Signals";
      reason = "Located near a major public transit hub, station, terminal or metropolitan transit lines for easy accessibility.";
      source = "Geographic Analysis";
      confidence = "Basic";
      break;
    case "Premium Employer Rank":
      category = "Growth Signals";
      reason = "This employer boasts an exceptional rating average (>= 4.5), suggesting high employee satisfaction and top-tier local operations.";
      source = "Google Maps Listing";
      confidence = "Basic";
      break;
    case "Fast Onboarding Likely":
      category = "Growth Signals";
      reason = "High-velocity consumer category paired with a stellar reputation indicates organized, streamlined, and fast onboarding processes.";
      source = "Google Maps Listing / Industry Standards";
      confidence = "Improved";
      break;
    case "Min Age: 21+ Required":
      category = "Warnings";
      reason = "The business category (such as bar, winery, brewery, or adult lounge) implies a strict legal age minimum of 21+ to work on-site.";
      source = "Industry Classification";
      confidence = "Improved";
      break;
    case "Ages 16+ Welcome":
      category = "Growth Signals";
      reason = "Highly youth-friendly category (like casual dining, retail, or local recreation) that actively hires teenagers and young adults.";
      source = "Industry Classification";
      confidence = "Improved";
      break;
    case "Ages 18+ Preferred":
      category = "Growth Signals";
      reason = "Standard business operation environment where legal age of majority (18+) is preferred for insurance and liability reasons.";
      source = "Industry Classification";
      confidence = "Improved";
      break;
  }

  return { name: tagName, category, reason, source, confidence };
}

export const MAJOR_NATIONAL_CHAINS = [
  "mcdonald", "mcdonald's", "starbucks", "walmart", "target", "home depot", "costco",
  "chipotle", "subway", "cvs", "walgreens", "best buy", "fedex", "ups", "the ups store",
  "amazon", "lowe's", "lowes", "dunkin", "burger king", "wendy's", "wendys", "taco bell",
  "chick-fil-a", "trader joe's", "trader joes", "kroger", "whole foods", "marriott",
  "hilton", "apple", "nike", "domino's", "dominos", "pizza hut", "papa john's", "papa johns",
  "panera", "five guys", "panda express", "outback steakhouse", "olive garden", "applebee's",
  "texas roadhouse", "red lobster", "buffalo wild wings", "planet fitness", "la fitness",
  "staples", "office depot", "rite aid", "petco", "petsmart", "nordstrom", "macy's",
  "macys", "kohl's", "kohls", "tj maxx", "marshalls", "ross", "old navy", "gap",
  "sephora", "ulta", "hertz", "enterprise", "carmax", "autozone", "dollar general",
  "family dollar", "dollar tree", "7-eleven", "wawa", "sheetz", "circle k", "chevron",
  "shell", "bp", "exxon", "mobil", "chase", "bank of america", "wells fargo", "citibank",
  "verizon", "at&t", "t-mobile"
];

export function isHighCompetitionBusiness(lead: BusinessBusiness): boolean {
  if (!lead || !lead.name) return false;
  const nameLower = lead.name.toLowerCase().trim();
  const reviewsCount = lead.reviewsCount || 0;
  const rating = lead.rating || 0;

  // 1. Check for major national chain / famous brand recognition
  const isMajorChain = MAJOR_NATIONAL_CHAINS.some(brand => {
    if (nameLower === brand) return true;
    if (nameLower.startsWith(brand + " ") || nameLower.endsWith(" " + brand) || nameLower.includes(" " + brand + " ")) return true;
    if (brand.length >= 5 && nameLower.includes(brand)) return true;
    return false;
  });

  if (isMajorChain) return true;

  // 2. Heavy review count (>= 300) with corporate or national presence indicators
  const isCorporateName = ["corp", "inc", "corporation", "llc", "group", "holdings", "brand", "brands", "international", "usa", "enterprises"].some(term => nameLower.includes(term));
  if (reviewsCount >= 300 && (isCorporateName || rating >= 4.3)) {
    return true;
  }

  return false;
}

/**
 * Calculates a comprehensive prospect score from 0 to 100 representing sales opportunity value,
 * based on concrete evidence-based categories (Contactability, Growth, Opportunity, Business Quality, Risk/Warnings).
 * Uses clear, plain-English breakdown reasons.
 */
export function analyzeBusinessProspect(lead: BusinessBusiness, distanceInMiles?: number | null): {
  score: number;
  scoreBreakdown: ScoreBreakdownItem[];
  scoreConfidence: 'Basic Score' | 'Improved Score' | 'Full Score';
  missingDataNotes: string[];
  tags: string[];
  pitchAngle: string;
  evidence: string[];
  isAdult: boolean;
  categorizedSignals: CategorizedSignals;
  tagExplanations: Record<string, TagExplanation>;
  scores: {
    trustScore: number;
    trustBreakdown: string[];
    convenienceScore: number;
    convenienceBreakdown: string[];
    hiringScore: number;
    hiringBreakdown: string[];
    opportunityScore: number;
    opportunityBreakdown: string[];
    sponsoredScore: number;
    sponsoredBreakdown: string[];
  };
  whyThisBusinessStandsOut: string[];
  whyThisJobStandsOut: string[];
  plainTags: {
    trust: string[];
    convenience: string[];
    hiring: string[];
    opportunity: string[];
    sponsored: string[];
  };
  strengths: string[];
  opportunitiesToImprove: string[];
  recommendedNextStep: string;
} {
  const workOpportunities: string[] = [];
  const growthSignals: string[] = [];
  const serviceOpportunities: string[] = [];
  const contactSignals: string[] = [];
  const warnings: string[] = [];
  const aiAutomation: string[] = [];

  const nameLower = lead.name.toLowerCase();
  const addressLower = (lead.address || "").toLowerCase();
  const catLower = (lead.category || "").toLowerCase();
  const webLower = (lead.website || "").toLowerCase();
  const audit = lead.websiteAudit;

  // --- 1. WORK OPPORTUNITIES (Hiring / Growth) ---
  const isHighHiringIndustry = ["restaurant", "cafe", "dental", "cleaning", "plumber", "contractor", "spa", "salon", "boutique", "hvac", "builder", "remodel", "construction", "moving", "roofing", "electrician", "medical", "hospital", "hotel"].some(r => catLower.includes(r));
  if (isHighHiringIndustry && lead.rating !== undefined && lead.rating >= 4.0) {
    workOpportunities.push("Currently Hiring");
  }
  const isMultipleRolesIndustry = ["restaurant", "medical", "hospital", "hotel", "supermarket", "retail"].some(r => catLower.includes(r));
  if (isMultipleRolesIndustry && lead.rating !== undefined && lead.rating >= 4.4) {
    workOpportunities.push("Hiring Multiple Roles");
  }
  if (lead.reviewsCount !== undefined && lead.reviewsCount > 0 && lead.reviewsCount <= 3) {
    workOpportunities.push("New Business");
  }
  const hasMultiTerm = ["group", "suites", "network", "partners", "associates", "locations", "and sons", "brothers"].some(term => nameLower.includes(term));
  if (hasMultiTerm || addressLower.includes("suite") || addressLower.includes("ste ")) {
    workOpportunities.push("Expanding Location");
  }
  const isServiceLabor = ["construction", "cleaning", "moving", "roofing", "electrician", "plumbing", "landscaping", "tree"].some(r => catLower.includes(r));
  if (isServiceLabor && lead.rating !== undefined && lead.rating >= 4.2) {
    workOpportunities.push("High Hiring Signal");
  }
  const isSeasonal = ["landscape", "tree", "garden", "pool", "roofing", "paint", "moving", "camp", "summer", "winter", "ski"].some(r => catLower.includes(r));
  if (isSeasonal) {
    workOpportunities.push("Seasonal Hiring Likely");
  }

  // --- 2. GROWTH SIGNALS (Quality / Market) ---
  if (lead.rating !== undefined && lead.rating >= 4.5 && lead.reviewsCount && lead.reviewsCount > 30) {
    growthSignals.push("Strong Reviews");
  }
  if (lead.rating !== undefined && lead.rating > 0 && lead.rating < 4.0 && lead.reviewsCount && lead.reviewsCount > 3) {
    growthSignals.push("Weak Reviews");
  }
  if ((lead.reviewsCount && lead.reviewsCount > 150) || (lead.rating !== undefined && lead.rating >= 4.8)) {
    growthSignals.push("High Local Demand");
  }
  if (isHighCompetitionBusiness(lead)) {
    growthSignals.push("High Competition");
  }
  const isMetro = ["ny", "ca", "tx", "fl", "il", "pa", "oh", "ga", "nc", "mi", "wa", "chicago", "york", "angeles", "houston", "miami"].some(metro => addressLower.includes(metro));
  if (isMetro && isHighHiringIndustry) {
    growthSignals.push("High Competition Area");
  }
  if (lead.rating !== undefined && lead.rating >= 4.3 && lead.reviewsCount && lead.reviewsCount < 15 && !lead.website) {
    growthSignals.push("Under-Marketed Business");
  }
  if (lead.phone && lead.rating !== undefined && lead.rating >= 4.0) {
    growthSignals.push("Good Outreach Target");
  }
  const isUrgentSupport = ["locksmith", "towing", "roadside", "plumber", "hvac", "electrician", "dentist", "emergency"].some(r => catLower.includes(r));
  if (isUrgentSupport && lead.rating !== undefined && lead.rating >= 4.0) {
    growthSignals.push("Fast Response Likely");
  }
  if (lead.phone && !nameLower.includes("corp") && !nameLower.includes("inc") && !addressLower.includes("suite")) {
    growthSignals.push("Decision Maker Likely Local");
  }

  // --- Dynamic Google Maps Listing Signals ---
  if (lead.rating !== undefined && lead.rating >= 4.7 && lead.reviewsCount !== undefined && lead.reviewsCount >= 50) {
    growthSignals.push("Elite Local Reputation");
  }
  if (lead.rating !== undefined && lead.rating >= 4.5 && (lead.reviewsCount === undefined || lead.reviewsCount <= 12)) {
    growthSignals.push("Untapped Growth Potential");
  }
  if (lead.reviewsCount === undefined || lead.reviewsCount <= 3) {
    growthSignals.push("Low Feedback Footprint");
  }
  if (lead.reviewsCount !== undefined && lead.reviewsCount >= 100) {
    growthSignals.push("High Consumer Engagement");
  }
  if (lead.rating === 5.0 && lead.reviewsCount !== undefined && lead.reviewsCount >= 4) {
    growthSignals.push("Pristine 5-Star Standing");
  }
  if (lead.rating !== undefined && lead.rating > 0 && lead.rating < 4.0 && lead.reviewsCount !== undefined && lead.reviewsCount >= 5) {
    growthSignals.push("Reputation Repair Prospect");
  }
  const isCorporateName = ["corp", "inc", "co ", "llc", "group", "associates"].some(term => nameLower.includes(term));
  if (lead.reviewsCount !== undefined && lead.reviewsCount > 0 && lead.reviewsCount < 10 && !isCorporateName) {
    growthSignals.push("Boutique Scale Operations");
  }
  if (!lead.website && !lead.phone) {
    growthSignals.push("Unclaimed Listing Opportunity");
  }

  // --- 3. SERVICE OPPORTUNITIES ---
  if (lead.website) {
    if (audit) {
      if (audit.failed || !audit.exists || lead.websiteReviewStatus === "failed") {
        serviceOpportunities.push("Website Review Failed");
      } else {
        // Check if the audit is outdated (older than 7 days, or marked outdated)
        const daysSince = audit.lastAudited 
          ? (new Date().getTime() - new Date(audit.lastAudited).getTime()) / (1000 * 3600 * 24)
          : 0;
        const isOutdated = daysSince > 7 || audit.reviewStatus === "outdated" || lead.websiteReviewStatus === "outdated";
        
        if (isOutdated) {
          serviceOpportunities.push("Website Review Outdated");
        }

        if (!audit.bookingButtonFound) {
          serviceOpportunities.push("Needs Online Booking");
        }
        if (!audit.contactFormFound) {
          serviceOpportunities.push("Needs Better Follow-Up");
        }
        if (!audit.socialLinksFound || audit.socialLinksFound.length === 0) {
          serviceOpportunities.push("Needs Social Media Help");
        }
        
        // Find if there are actual issues (Only show "Needs Website Cleanup" if successful audit has issues)
        const hasActualIssues = 
          !audit.contactFormFound ||
          !audit.bookingButtonFound ||
          !audit.mobileFriendly ||
          !audit.https ||
          audit.outdatedDetails ||
          !audit.seoTitleExists ||
          !audit.metaDescriptionExists;

        if (hasActualIssues) {
          serviceOpportunities.push("Needs Website Cleanup");
        }
      }
    } else {
      serviceOpportunities.push("Website Not Reviewed Yet");
    }
  }
  if (lead.rating !== undefined && lead.rating > 0 && lead.rating < 4.2) {
    serviceOpportunities.push("Needs Review Help");
  }
  if (lead.rating !== undefined && lead.rating >= 4.5 && (!lead.website || (lead.reviewsCount && lead.reviewsCount < 20))) {
    serviceOpportunities.push("Needs Local Ads");
  }
  if (lead.website && audit && !audit.failed && audit.exists && (audit.outdatedDetails || !audit.socialLinksFound || audit.socialLinksFound.length === 0)) {
    serviceOpportunities.push("Needs Better Photos");
  }
  if (!lead.phone && lead.website) {
    serviceOpportunities.push("Needs Customer Support Help");
  }

  // --- Dynamic System Analysis Service Gaps ---
  if (lead.website && audit && !audit.failed && audit.exists) {
    serviceOpportunities.push("Crawler Audit: Website Verified");
    
    if (audit.https === true) {
      serviceOpportunities.push("Secure SSL Encryption Found");
    } else if (audit.https === false) {
      serviceOpportunities.push("SSL Security Risk");
    }

    if (audit.mobileFriendly === true) {
      serviceOpportunities.push("Mobile Responsive Layout");
    } else if (audit.mobileFriendly === false) {
      serviceOpportunities.push("Mobile-Friendliness Gap");
    }

    if (audit.contactFormFound === true) {
      serviceOpportunities.push("Lead-Capture Form Active");
    } else if (audit.contactFormFound === false) {
      serviceOpportunities.push("Missing Digital Form Risk");
    }

    if (audit.bookingButtonFound === true) {
      serviceOpportunities.push("Automated Scheduling Online");
    } else if (audit.bookingButtonFound === false) {
      serviceOpportunities.push("Booking System Gap");
    }

    if (audit.seoTitleExists === true) {
      serviceOpportunities.push("HTML SEO Title Present");
    } else if (audit.seoTitleExists === false) {
      serviceOpportunities.push("SEO Optimization Opportunity");
    }

    if (audit.outdatedDetails === true) {
      serviceOpportunities.push("Outdated Content Exposure");
    }
  }

  // --- 4. CONTACT SIGNALS ---
  if (lead.phone) {
    contactSignals.push("Direct Voice Channel");
  }
  if (lead.contacts && lead.contacts.length > 0) {
    contactSignals.push("Verified Contact Integrity");
    const rolesLower = lead.contacts.map((c) => (c.role || "").toLowerCase());
    const isDM = rolesLower.some((r) => r.includes("owner") || r.includes("founder") || r.includes("ceo") || r.includes("manager") || r.includes("director") || r.includes("president") || r.includes("exec"));
    if (isDM) {
      contactSignals.push("Verified Decision-Maker Found");
    }

    const isOwner = rolesLower.some((r) => r.includes("owner") || r.includes("founder") || r.includes("ceo"));
    const isManager = rolesLower.some((r) => r.includes("manager") || r.includes("director") || r.includes("president") || r.includes("exec"));

    if (isOwner) {
      contactSignals.push("Owner/manager contact found");
    } else if (isManager) {
      contactSignals.push("Owner/manager contact found");
    } else {
      contactSignals.push("Direct contact found");
    }

    const hasHighConfidence = lead.contacts.some(c => c.confidence === "High" && c.email);
    if (hasHighConfidence) {
      contactSignals.push("Easy to Contact");
    }
    const hasLowConfidence = lead.contacts.some(c => c.confidence === "Low");
    if (hasLowConfidence) {
      contactSignals.push("Needs Contact Review");
    }
    contactSignals.push("Ready for Outreach");
  }

  // --- 5. WARNINGS ---
  const chains = ["mcdonald's", "starbucks", "walmart", "target", "subway", "dunkin", "pizza hut", "domino's", "taco bell", "burger king", "7-eleven", "walgreens", "cvs"];
  if (chains.some((c) => nameLower.includes(c))) {
    warnings.push("Possible Franchise");
  }
  if (lead.phone && lead.phone.length > 5 && nameLower.includes("agency")) {
    warnings.push("Possible Duplicate");
  }
  if (lead.rating === undefined && !lead.phone && !lead.website) {
    warnings.push("Low Confidence Data");
  }
  if (lead.contacts && lead.contacts.some(c => c.confidence === "Low")) {
    warnings.push("Contact Info Needs Review");
  }
  if (lead.websiteAudit && !lead.websiteAudit.exists) {
    warnings.push("Website Check Failed");
  }
  if (lead.crawlStatus === "error") {
    warnings.push("Contact Search Failed");
  }
  const ADULT_KEYWORDS = ["adult", "erotic", "xxx", "sensual", "strip club", "gentlemens club", "escort service", "sensual massage", "porn", "night club", "cannabis", "dispensary", "marijuana"];
  const isAdult = ADULT_KEYWORDS.some(kw => nameLower.includes(kw) || webLower.includes(kw) || addressLower.includes(kw));
  if (lead.rating !== undefined && lead.rating > 0 && lead.rating < 2.5) {
    warnings.push("Possibly Closed");
  }

  // --- 6. AI / AUTOMATION SIGNALS ---
  const mentionsAI =
    nameLower.includes("ai ") ||
    nameLower.includes(" ai") ||
    nameLower.includes("chatbot") ||
    nameLower.includes("artificial intelligence") ||
    nameLower.includes("gpt") ||
    (lead.websiteAudit?.seoTitle || "").toLowerCase().includes("ai") ||
    (lead.websiteAudit?.metaDescription || "").toLowerCase().includes("ai") ||
    (lead.pitchAngle && lead.pitchAngle.toLowerCase().includes("chatbot"));

  const hasChatbot =
    nameLower.includes("chatbot") || (lead.websiteAudit?.contactPageFound && nameLower.includes("bot"));

  if (mentionsAI || hasChatbot) {
    aiAutomation.push("Known AI Usage");
  }

  let hasAiSignal = mentionsAI || hasChatbot;

  if (lead.website) {
    if (audit) {
      if (!audit.bookingButtonFound) {
        aiAutomation.push("Automation Opportunity");
        aiAutomation.push("Manual Booking Likely");
        hasAiSignal = true;
      }
      if (!audit.contactFormFound) {
        aiAutomation.push("Website Chat Opportunity");
        hasAiSignal = true;
      }
    } else {
      aiAutomation.push("Automation Opportunity");
      hasAiSignal = true;
    }
  }

  if (lead.rating !== undefined && lead.rating > 0 && lead.rating < 4.5) {
    aiAutomation.push("Automation Opportunity");
    aiAutomation.push("Review Reply Automation Opportunity");
    hasAiSignal = true;
  }

  const isHighUrgencyService = ["locksmith", "towing", "roadside", "plumbing", "plumber", "hvac", "electrician", "dentist", "emergency", "taxi", "moving", "roofing"].some(r => catLower.includes(r));
  if (lead.phone && isHighUrgencyService) {
    aiAutomation.push("Automation Opportunity");
    aiAutomation.push("Missed Call Follow-Up Opportunity");
    hasAiSignal = true;
  }

  if (audit && audit.contactFormFound && !audit.bookingButtonFound) {
    aiAutomation.push("Automation Opportunity");
    aiAutomation.push("Quote Request Automation Opportunity");
    hasAiSignal = true;
  }

  if (!hasAiSignal) {
    aiAutomation.push("No AI Signal Found");
  }

  // Deduplicate and process tag groups in order of highest priority using a global seen Set
  const seenTags = new Set<string>();

  const finalWarnings: string[] = [];
  for (const t of warnings) {
    if (t && !seenTags.has(t)) {
      finalWarnings.push(t);
      seenTags.add(t);
    }
  }

  const finalWork: string[] = [];
  for (const t of workOpportunities) {
    if (t && !seenTags.has(t)) {
      finalWork.push(t);
      seenTags.add(t);
    }
  }

  const finalGrowth: string[] = [];
  for (const t of growthSignals) {
    if (t && !seenTags.has(t)) {
      finalGrowth.push(t);
      seenTags.add(t);
    }
  }

  const finalService: string[] = [];
  for (const t of serviceOpportunities) {
    if (t && !seenTags.has(t)) {
      finalService.push(t);
      seenTags.add(t);
    }
  }

  const finalContact: string[] = [];
  for (const t of contactSignals) {
    if (t && !seenTags.has(t)) {
      finalContact.push(t);
      seenTags.add(t);
    }
  }

  const finalAiAutomation: string[] = [];
  for (const t of aiAutomation) {
    if (t && !seenTags.has(t)) {
      finalAiAutomation.push(t);
      seenTags.add(t);
    }
  }

  // --- EVIDENCE-BASED SCORE MODEL (Smarter scoring based on rating, reviews, website quality, contact options, profile completeness, etc.) ---
  const breakItems: ScoreBreakdownItem[] = [];

  // A. Base Starting Point
  let baseScore = 40;
  breakItems.push({ label: "Base prospect status", points: 40 });

  // 1. Google Rating
  if (lead.rating !== undefined) {
    if (lead.rating >= 4.5) {
      baseScore += 10;
      breakItems.push({ label: `Elite local rating (${lead.rating.toFixed(1)}★)`, points: 10 });
    } else if (lead.rating >= 4.0) {
      baseScore += 5;
      breakItems.push({ label: `Solid local rating (${lead.rating.toFixed(1)}★)`, points: 5 });
    } else if (lead.rating > 0 && lead.rating < 3.5) {
      baseScore -= 10;
      breakItems.push({ label: `Low local rating (${lead.rating.toFixed(1)}★ - Needs recovery)`, points: -10 });
    }
  }

  // 2. Review Count
  if (lead.reviewsCount !== undefined) {
    if (lead.reviewsCount >= 100) {
      baseScore += 12;
      breakItems.push({ label: `Deeply established customer trust (${lead.reviewsCount} reviews)`, points: 12 });
    } else if (lead.reviewsCount >= 30) {
      baseScore += 6;
      breakItems.push({ label: `Healthy review volume (${lead.reviewsCount} reviews)`, points: 6 });
    } else if (lead.reviewsCount > 0 && lead.reviewsCount < 10) {
      baseScore -= 8;
      breakItems.push({ label: `Low review volume (${lead.reviewsCount} reviews - Visibility gap)`, points: -8 });
    }
  }

  // 3. Profile Completeness (Website, Phone, Address)
  const hasWebsite = !!lead.website;
  const hasPhone = !!lead.phone;
  const hasAddress = !!lead.address;
  
  if (hasWebsite && hasPhone && hasAddress) {
    baseScore += 15;
    breakItems.push({ label: "Complete business listing profile", points: 15 });
  } else if (!hasWebsite && !hasPhone) {
    baseScore -= 12;
    breakItems.push({ label: "Severely incomplete business details", points: -12 });
  } else {
    baseScore += 5;
    breakItems.push({ label: "Partial business profile", points: 5 });
  }

  // 4. Website Quality & Gaps
  const hasWebsiteAuditRun = lead.websiteAudit && !lead.websiteAudit.failed;
  const isAuditFailed = lead.websiteAudit?.failed || lead.websiteReviewStatus === "failed";

  if (lead.website) {
    if (hasWebsiteAuditRun && audit && audit.exists && !audit.failed) {
      if (audit.mobileFriendly && audit.https) {
        baseScore += 10;
        breakItems.push({ label: "Secure and mobile-responsive website", points: 10 });
      }
      if (!audit.bookingButtonFound) {
        baseScore -= 5;
        breakItems.push({ label: "Lacks interactive booking/scheduler form", points: -5 });
      }
      if (!audit.contactFormFound) {
        baseScore -= 5;
        breakItems.push({ label: "Lacks homepage lead capture form", points: -5 });
      }
    } else if (isAuditFailed) {
      baseScore -= 10;
      breakItems.push({ label: "Broken online presence / Website review failed", points: -10 });
    }
  } else {
    baseScore -= 15;
    breakItems.push({ label: "Missing business website", points: -15 });
  }

  // 5. Contact Options & Verification Gaps
  const positionsFound = (lead.contacts || []).map(c => (c.role || "").toLowerCase());
  const isOwnerConfirmed = positionsFound.some(r => r.includes("owner") || r.includes("founder") || r.includes("ceo"));
  const isManagerConfirmed = positionsFound.some(r => r.includes("manager") || r.includes("director") || r.includes("president") || r.includes("exec"));

  if (isOwnerConfirmed || isManagerConfirmed) {
    baseScore += 15;
    breakItems.push({ label: "Verified decision-maker contact available", points: 15 });
  } else if (lead.contacts && lead.contacts.length > 0) {
    baseScore += 8;
    breakItems.push({ label: "Direct team email available", points: 8 });
  } else if (lead.crawlStatus === "error" || lead.crawlStatus === "idle") {
    baseScore -= 5;
    breakItems.push({ label: "No direct contact emails found", points: -5 });
  }

  // 6. Hiring Signals
  const isHiring = finalWork.includes("Currently Hiring") || finalWork.includes("Hiring Multiple Roles");
  if (isHiring) {
    baseScore += 10;
    breakItems.push({ label: "Active hiring signal detected", points: 10 });
  }

  // 7. Local Demand (Category)
  if (isHighHiringIndustry) {
    baseScore += 10;
    breakItems.push({ label: "High local service demand category", points: 10 });
  }

  // 8. Sponsor Relevance
  if (lead.Businessescore !== undefined && lead.Businessescore > 80) {
    baseScore += 8;
    breakItems.push({ label: "Active premium sponsored alignment", points: 8 });
  }

  // C. Serious Warnings / Penalties
  let penaltyPoints = 0;
  if (finalWarnings.includes("Possibly Closed")) {
    penaltyPoints += 25;
    breakItems.push({ label: "Warning: Possibly closed entity", points: -25 });
  }
  if (finalWarnings.includes("Possible Duplicate")) {
    penaltyPoints += 15;
    breakItems.push({ label: "Warning: Possible duplicate listing", points: -15 });
  }
  if (finalWarnings.includes("Low Confidence Data")) {
    penaltyPoints += 15;
    breakItems.push({ label: "Warning: Low confidence data quality", points: -15 });
  }

  const score = Math.max(10, Math.min(100, Math.round(baseScore - penaltyPoints)));

  let scoreConfidence: 'Basic Score' | 'Improved Score' | 'Full Score' = 'Basic Score';
  const hasContactSearchRun = lead.crawlStatus === "success" || lead.crawlStatus === "error";
  if (hasWebsiteAuditRun && hasContactSearchRun) {
    scoreConfidence = 'Full Score';
  } else if (hasWebsiteAuditRun || hasContactSearchRun) {
    scoreConfidence = 'Improved Score';
  } else {
    scoreConfidence = 'Basic Score';
  }

  const missingDataNotes: string[] = [];
  if (lead.website && !hasWebsiteAuditRun && !isAuditFailed) {
    missingDataNotes.push("Website review not run yet");
  }
  if (!hasContactSearchRun) {
    missingDataNotes.push("Contact search not run yet");
  }

  // Determine dynamic outreach suggestion & evidence
  let pitchAngle = "";
  const evidence: string[] = [];

  const isLocalService = ["dentist", "doctor", "restaurant", "cafe", "salon", "spa", "plumber", "electrician", "contractor", "landscaper", "cleaning", "hvac", "moving", "towing", "locksmith", "roofing"].some(r => catLower.includes(r));

  if (scoreConfidence === "Basic Score" || (!lead.website && !lead.contacts?.length && !audit)) {
    pitchAngle = "Not enough data yet. Run Website Review or Search Contacts to generate a more specific outreach idea.";
  } else {
    if (!lead.website) {
      pitchAngle = "Offer a clean, modern landing page package to help this business capture local online searches. With positive customer reputation but no website, they are missing out on highly motivated local leads.";
      evidence.push("Missing business website");
      if (isLocalService) evidence.push("Local service business");
      if (lead.phone) evidence.push("Phone-based business type");
    } else if (audit && (audit.failed || !audit.exists)) {
      pitchAngle = "Offer a consultation to restore their offline or unstable corporate webpage so they stop losing prospective local leads.";
      evidence.push("Website review failed");
      if (isLocalService) evidence.push("Local service business");
    } else if (lead.website && (audit?.mobileFriendly === false || audit?.https === false || audit?.outdatedDetails)) {
      pitchAngle = "Help them clean up their website so it displays perfectly on mobile devices, secures transactions via HTTPS, and updates stale corporate information.";
      if (audit?.mobileFriendly === false) evidence.push("Website review found mobile friendly issues");
      if (audit?.https === false) evidence.push("Website review found not HTTPS");
      if (audit?.outdatedDetails) evidence.push("Website review found outdated details");
      if (isLocalService) evidence.push("Local service business");
      if (lead.phone) evidence.push("Phone-based business type");
    } else if (lead.website && audit && !audit.contactFormFound) {
      pitchAngle = "Offer a simple quote request page or integrate a clean lead-capture form to help this business capture and convert more client leads.";
      evidence.push("Website review found no contact form");
      if (isLocalService) evidence.push("Local service business");
      if (lead.phone) evidence.push("Phone-based business type");
    } else if (lead.website && audit && !audit.bookingButtonFound) {
      pitchAngle = "Offer a direct calendar integration widget to allow prospective clients to reserve dates and times on autopilot.";
      evidence.push("Website review found no booking/contact path");
      if (isLocalService) evidence.push("Local service business");
      if (lead.phone) evidence.push("Phone-based business type");
    } else if (finalWork.includes("Currently Hiring") || finalWork.includes("Hiring Multiple Roles")) {
      pitchAngle = "Deliver a micro-careers portal integration or helper tracking form to streamline their active hiring pipeline.";
      evidence.push("Active hiring signals detected");
      if (isLocalService) evidence.push("Local service business");
      if (lead.phone) evidence.push("Phone-based business type");
    } else if (lead.contacts && lead.contacts.length > 0) {
      const mainContact = lead.contacts[0];
      pitchAngle = `Reach out directly to ${mainContact.name} (${mainContact.role || "Decision Maker"}) with a dynamic digital audit, bypassing gatekeepers entirely.`;
      evidence.push("Owner/manager contact found");
      if (isLocalService) evidence.push("Local service business");
    } else {
      pitchAngle = "Propose an automated review generation manager to help them systematically collect, response-track, and publish local customer reviews.";
      evidence.push("Needs automated customer reviews follow-up workflow");
      if (isLocalService) evidence.push("Local service business");
    }
  }

  // --- Dynamic Location Appeal & Age Requirement Signals for Job Finders ---
  // A. Location appeal signals
  if (distanceInMiles !== null && distanceInMiles !== undefined) {
    if (distanceInMiles <= 1.5) {
      if (!finalGrowth.includes("Immediate Walk-In Range")) {
        finalGrowth.push("Immediate Walk-In Range");
      }
    } else if (distanceInMiles <= 5.0) {
      if (!finalGrowth.includes("Sub-5 Mile Commute Proximity")) {
        finalGrowth.push("Sub-5 Mile Commute Proximity");
      }
    } else if (distanceInMiles > 10.0) {
      if (!finalGrowth.includes("Regional Hub Coverage")) {
        finalGrowth.push("Regional Hub Coverage");
      }
    }
  }

  const isWalkable = ["suite", "ste ", "main", "ave", "broadway", "mall", "plaza", "center", "market", "boulevard", "blvd", "st "].some(term => addressLower.includes(term));
  if (isWalkable) {
    if (!finalGrowth.includes("Walkable Area")) {
      finalGrowth.push("Walkable Area");
    }
    if (!finalGrowth.includes("High Walkability Score")) {
      finalGrowth.push("High Walkability Score");
    }
  }

  const isTransitFriendly = ["metro", "downtown", "city", "ny", "sf", "la", "seattle", "boston", "chicago", "transit", "station", "terminal"].some(term => addressLower.includes(term) || nameLower.includes(term));
  if (isTransitFriendly) {
    if (!finalGrowth.includes("Public Transit Access")) {
      finalGrowth.push("Public Transit Access");
    }
    if (!finalGrowth.includes("Transit-Oriented Corridor")) {
      finalGrowth.push("Transit-Oriented Corridor");
    }
  }

  const isGroundFloor = ["suite a", "ste a", "ste 100", "suite 100", "ground", "fl 1", "unit 1", "suite 1", "lobby", "bay "].some(term => addressLower.includes(term));
  if (isGroundFloor) {
    if (!finalGrowth.includes("Ground Floor Access")) {
      finalGrowth.push("Ground Floor Access");
    }
  }

  const isHighlyRated = lead.rating !== undefined && lead.rating >= 4.5;
  if (isHighlyRated) {
    if (!finalGrowth.includes("Premium Employer Rank")) {
      finalGrowth.push("Premium Employer Rank");
    }
  }

  const isQuickHiring = ["restaurant", "cafe", "cleaning", "plumber", "contractor", "spa", "salon", "boutique", "moving", "retail"].some(r => catLower.includes(r));
  if (isQuickHiring && isHighlyRated) {
    if (!finalGrowth.includes("Fast Onboarding Likely")) {
      finalGrowth.push("Fast Onboarding Likely");
    }
  }

  if (isMetro) {
    if (!finalGrowth.includes("Metro Commercial Density Advantage")) {
      finalGrowth.push("Metro Commercial Density Advantage");
    }
  } else if (lead.rating !== undefined && lead.rating >= 4.0) {
    if (!finalGrowth.includes("Suburban Community Sanctuary")) {
      finalGrowth.push("Suburban Community Sanctuary");
    }
  }

  // B. Age requirement signals
  const ADULT_LIMIT_KEYWORDS = ["dispensary", "cannabis", "marijuana", "bar", "lounge", "pub", "taverns", "liquor", "night club", "strip club", "gentlemens club", "winery", "brewery", "xxx", "porn", "escort"];
  const isAdultOnly = ADULT_LIMIT_KEYWORDS.some(kw => nameLower.includes(kw) || catLower.includes(kw) || webLower.includes(kw));
  if (isAdultOnly) {
    if (!finalWarnings.includes("Min Age: 21+ Required")) {
      finalWarnings.push("Min Age: 21+ Required");
    }
  } else {
    // Check if youth-friendly categories
    const isTeensFriendly = ["restaurant", "cafe", "boutique", "salon", "spa", "retail", "recreation", "entertainment", "camp", "summer", "pool"].some(r => catLower.includes(r));
    if (isTeensFriendly) {
      if (!finalGrowth.includes("Ages 16+ Welcome")) {
        finalGrowth.push("Ages 16+ Welcome");
      }
    } else {
      if (!finalGrowth.includes("Ages 18+ Preferred")) {
        finalGrowth.push("Ages 18+ Preferred");
      }
    }
  }

  const tags = Array.from(
    new Set([
      ...finalWork,
      ...finalGrowth,
      ...finalService,
      ...finalContact,
      ...finalWarnings,
    ])
  ).filter(Boolean);

  // --- CALCULATE BIZNEARYOU SCORES (TRUST, CONVENIENCE, HIRING SIGNAL, OPPORTUNITY, SPONSORED RELEVANCE) ---
  
  // 1. TRUST SCORE (rating, review count, website, phone, photos exists, completeness, status)
  let trustScore = 10;
  const trustBreakdown: string[] = [];
  
  if (lead.rating !== undefined) {
    if (lead.rating >= 4.7) {
      trustScore += 25;
      trustBreakdown.push(`Elite rating average of ${lead.rating.toFixed(1)} stars (Tier 1 local reputation) (+25 pts)`);
    } else if (lead.rating >= 4.2) {
      trustScore += 20;
      trustBreakdown.push(`Strong user rating of ${lead.rating.toFixed(1)} stars (+20 pts)`);
    } else if (lead.rating >= 3.0) {
      trustScore += 10;
      trustBreakdown.push(`Average user rating of ${lead.rating.toFixed(1)} stars (+10 pts)`);
    } else {
      trustScore -= 15;
      trustBreakdown.push(`Customer rating below 3.0 (${lead.rating.toFixed(1)}★) indicates operational friction (-15 pts)`);
    }
  } else {
    trustScore += 10;
    trustBreakdown.push("No official rating average recorded (+10 pts)");
  }

  if (lead.reviewsCount !== undefined) {
    if (lead.reviewsCount >= 200) {
      trustScore += 25;
      trustBreakdown.push(`Massive consumer social proof with ${lead.reviewsCount} reviews (+25 pts)`);
    } else if (lead.reviewsCount >= 50) {
      trustScore += 20;
      trustBreakdown.push(`Robust feedback sample size with ${lead.reviewsCount} reviews (+20 pts)`);
    } else if (lead.reviewsCount >= 10) {
      trustScore += 15;
      trustBreakdown.push(`Established local profile footprint with ${lead.reviewsCount} reviews (+15 pts)`);
    } else {
      trustScore += 5;
      trustBreakdown.push(`Boutique feedback baseline (${lead.reviewsCount} reviews) (+5 pts)`);
    }
  } else {
    trustScore += 5;
    trustBreakdown.push("No reviews count recorded on profile (+5 pts)");
  }

  if (lead.website) {
    trustScore += 20;
    trustBreakdown.push("Official business website exists and is active (+20 pts)");
  } else {
    trustBreakdown.push("Lacks an official website listing (+0 pts)");
  }

  if (lead.phone) {
    trustScore += 15;
    trustBreakdown.push("Direct telephone contact line available (+15 pts)");
  } else {
    trustBreakdown.push("No phone number registered (+0 pts)");
  }

  if (lead.address && lead.category) {
    trustScore += 15;
    trustBreakdown.push("Complete and claimed business directory profile (+15 pts)");
  } else {
    trustScore += 5;
    trustBreakdown.push("Incomplete directory listing credentials (+5 pts)");
  }
  trustScore = Math.max(0, Math.min(100, trustScore));

  // 2. CONVENIENCE SCORE (distance, open now, hours, phone, website, booking/form)
  let convenienceScore = 15;
  const convenienceBreakdown: string[] = [];

  if (distanceInMiles !== null && distanceInMiles !== undefined) {
    if (distanceInMiles <= 1.5) {
      convenienceScore += 35;
      convenienceBreakdown.push(`Immediate range: only ${distanceInMiles.toFixed(2)} miles away (+35 pts)`);
    } else if (distanceInMiles <= 5.0) {
      convenienceScore += 25;
      convenienceBreakdown.push(`Sub-5 mile convenient commute (${distanceInMiles.toFixed(1)} miles) (+25 pts)`);
    } else if (distanceInMiles <= 10.0) {
      convenienceScore += 15;
      convenienceBreakdown.push(`Regional drive range (${distanceInMiles.toFixed(1)} miles) (+15 pts)`);
    } else {
      convenienceScore += 5;
      convenienceBreakdown.push(`Extended metropolitan range (${distanceInMiles.toFixed(1)} miles) (+5 pts)`);
    }
  } else {
    convenienceScore += 15;
    convenienceBreakdown.push("General search hub proximity (+15 pts)");
  }

  const isClosed = finalWarnings.includes("Possibly Closed") || (lead.rating !== undefined && lead.rating > 0 && lead.rating < 2.5);
  if (isClosed) {
    convenienceScore -= 20;
    convenienceBreakdown.push("Listing flagged as potentially closed or restricted (-20 pts)");
  } else {
    convenienceScore += 20;
    convenienceBreakdown.push("Standard operational and active business hours (+20 pts)");
  }

  if (lead.website) {
    convenienceScore += 15;
    convenienceBreakdown.push("Direct website listed for instant information (+15 pts)");
  }
  if (lead.phone) {
    convenienceScore += 15;
    convenienceBreakdown.push("Direct phone button listed for instant contact (+15 pts)");
  }

  if (lead.websiteAudit?.bookingButtonFound) {
    convenienceScore += 15;
    convenienceBreakdown.push("Online booking button found for instant appointment scheduling (+15 pts)");
  } else if (lead.websiteAudit?.contactFormFound) {
    convenienceScore += 10;
    convenienceBreakdown.push("Online contact or quote request form active (+10 pts)");
  }
  convenienceScore = Math.max(0, Math.min(100, convenienceScore));

  // 3. HIRING SIGNAL SCORE (Strictly grounded in verified local public evidence; no generic category or rating assumptions alone)
  const evidences = (lead.hiringEvidences && lead.hiringEvidences.length > 0)
    ? lead.hiringEvidences
    : extractAndVerifyHiringEvidence(lead);

  lead.hiringEvidences = evidences;

  const evidenceResult = calculateEvidenceGroundedHiringScore(evidences, lead);
  const hiringScore = evidenceResult.hiringScore;
  const hiringBreakdown: string[] = evidenceResult.hiringBreakdown;

  // 4. OPPORTUNITY SCORE (no website, poor website audit, no contact form, no booking button, high rating + weak web)
  let opportunityScore = 10;
  const opportunityBreakdown: string[] = [];

  if (!lead.website) {
    opportunityScore += 35;
    opportunityBreakdown.push("Lacks an official website (Massive web design prospect!) (+35 pts)");
  } else if (lead.websiteAudit?.failed || lead.websiteReviewStatus === "failed") {
    opportunityScore += 25;
    opportunityBreakdown.push("Existing website is broken or fails load checks (+25 pts)");
  } else if (lead.websiteAudit && !lead.websiteAudit.failed) {
    let auditScoreInc = 0;
    if (!lead.websiteAudit.mobileFriendly) {
      auditScoreInc += 10;
      opportunityBreakdown.push("Website is not mobile responsive (+10 pts)");
    }
    if (!lead.websiteAudit.https) {
      auditScoreInc += 10;
      opportunityBreakdown.push("Website lacks secure SSL encryption (+10 pts)");
    }
    if (lead.websiteAudit.outdatedDetails) {
      opportunityBreakdown.push("Website has outdated business details (+5 pts)");
      auditScoreInc += 5;
    }
    opportunityScore += Math.min(20, auditScoreInc);
  }

  if (lead.website && lead.websiteAudit && !lead.websiteAudit.bookingButtonFound) {
    opportunityScore += 20;
    opportunityBreakdown.push("Lacks online booking/scheduling button (E-commerce / reservation integration lead) (+20 pts)");
  }

  if (lead.website && lead.websiteAudit && !lead.websiteAudit.contactFormFound) {
    opportunityScore += 15;
    opportunityBreakdown.push("Lacks interactive lead capture form (+15 pts)");
  }

  const isHighRatingWeakWeb = lead.rating !== undefined && lead.rating >= 4.4 && (!lead.website || (lead.websiteAudit && (!lead.websiteAudit.mobileFriendly || !lead.websiteAudit.bookingButtonFound)));
  if (isHighRatingWeakWeb) {
    opportunityScore += 20;
    opportunityBreakdown.push("Elite local rating with a weak digital footprint (Perfect warm outreach pitch!) (+20 pts)");
  }

  if (lead.contacts && lead.contacts.length > 0) {
    opportunityScore += 10;
    opportunityBreakdown.push("Direct team contact information verified and available (+10 pts)");
  }
  opportunityScore = Math.max(0, Math.min(100, opportunityScore));

  // 5. SPONSORED RELEVANCE SCORE
  let sponsoredScore = 10;
  const sponsoredBreakdown: string[] = [];

  if (lead.Businessescore !== undefined && lead.Businessescore > 80) {
    sponsoredScore += 40;
    sponsoredBreakdown.push("Active premium sponsorship status with budget allocation (+40 pts)");
  } else {
    sponsoredScore += 10;
    sponsoredBreakdown.push("Standard promoted ad placement relevance (+10 pts)");
  }

  if (isHighHiringIndustry) {
    sponsoredScore += 30;
    sponsoredBreakdown.push("High alignment with local job/business search intent (+30 pts)");
  } else {
    sponsoredScore += 15;
    sponsoredBreakdown.push("Broad general business intent match (+15 pts)");
  }

  if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 5.0) {
    sponsoredScore += 20;
    sponsoredBreakdown.push("Prime hyper-local neighborhood geographic alignment (+20 pts)");
  } else {
    sponsoredScore += 10;
    sponsoredBreakdown.push("Wider metropolitan ad placement overlap (+10 pts)");
  }

  if (lead.rating !== undefined && lead.rating >= 4.4) {
    sponsoredScore += 10;
    sponsoredBreakdown.push("High quality ad rating multiplier boost (+10 pts)");
  }
  sponsoredScore = Math.max(0, Math.min(100, sponsoredScore));

  // Plain visual labels as requested
  const trustTags: string[] = [];
  if (lead.reviewsCount !== undefined && lead.reviewsCount >= 100) trustTags.push("Highly Reviewed");
  if (lead.rating !== undefined && lead.rating >= 4.5) trustTags.push("Strong Local Reputation");
  if (lead.reviewsCount === undefined || lead.reviewsCount <= 3) trustTags.push("New or Low-Review Listing");
  if (lead.website) trustTags.push("Website Available");
  if (lead.phone || (lead.contacts && lead.contacts.length > 0)) trustTags.push("Contact Info Available");

  const convenienceTags: string[] = [];
  if (!isClosed) convenienceTags.push("Open Now");
  if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 3.0) convenienceTags.push("Nearby");
  if (lead.phone) convenienceTags.push("Easy to Contact");
  if (lead.websiteAudit?.bookingButtonFound || lead.website) convenienceTags.push("Application Link Available");
  if (lead.category && isHighHiringIndustry) convenienceTags.push("Clear Pay Listed");

  const hasMultiUnit = nameLower.includes("group") || nameLower.includes("locations") || nameLower.includes("partners") || addressLower.includes("suite") || addressLower.includes("ste ");

  const hiringTags: string[] = [];
  if (isHighHiringIndustry) hiringTags.push("Likely Hiring Category");
  if (lead.websiteAudit?.bookingButtonFound) hiringTags.push("Careers Page Found");
  if (hasMultiUnit) hiringTags.push("Multiple Locations");
  if (isHighHiringIndustry) hiringTags.push("Long Business Hours");
  if (isSeasonal) hiringTags.push("Seasonal Hiring Likely");

  const opportunityTags: string[] = [];
  if (!lead.website) {
    opportunityTags.push("No Website Found");
  } else {
    if (lead.websiteAudit && (!lead.websiteAudit.mobileFriendly || !lead.websiteAudit.https)) {
      opportunityTags.push("Website Needs Review");
    }
    if (lead.websiteAudit && !lead.websiteAudit.bookingButtonFound) {
      opportunityTags.push("Booking Missing");
    }
    if (lead.websiteAudit && !lead.websiteAudit.contactFormFound) {
      opportunityTags.push("Contact Form Missing");
    }
  }
  if (isHighHiringIndustry) opportunityTags.push("High Demand Category");
  if (isMetro) opportunityTags.push("Competitor Pressure");

  const sponsoredTags: string[] = [];
  if (lead.Businessescore !== undefined && lead.Businessescore > 80) {
    sponsoredTags.push("Sponsored");
    sponsoredTags.push("Advertises Everywhere");
    if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 5.0) {
      sponsoredTags.push("Sponsored Near You");
    }
    sponsoredTags.push("Promoted Job");
  }

  // "Why this business stands out" / "Why this job stands out" checklists
  const whyThisBusinessStandsOut: string[] = [];
  if (lead.rating !== undefined && lead.reviewsCount !== undefined) {
    whyThisBusinessStandsOut.push(`${lead.rating.toFixed(1)} rating from ${lead.reviewsCount} reviews`);
  } else if (lead.rating !== undefined) {
    whyThisBusinessStandsOut.push(`${lead.rating.toFixed(1)} rating average on Google Maps`);
  }
  if (lead.website && lead.phone) {
    whyThisBusinessStandsOut.push("Website and phone number available");
  } else if (lead.website) {
    whyThisBusinessStandsOut.push("Official business website is available");
  } else if (lead.phone) {
    whyThisBusinessStandsOut.push("Direct phone contact line is available");
  }
  if (!isClosed) {
    whyThisBusinessStandsOut.push("Open now with active business hours");
  }
  if (isHighHiringIndustry) {
    whyThisBusinessStandsOut.push("High-demand category in this area");
  }
  if (lead.website && lead.websiteAudit && !lead.websiteAudit.bookingButtonFound) {
    whyThisBusinessStandsOut.push("Website has no booking button (conversion opportunity!)");
  }
  if (lead.contacts && lead.contacts.length > 0) {
    whyThisBusinessStandsOut.push("Verified decision-maker contact details found");
  }

  const whyThisJobStandsOut: string[] = [];
  whyThisJobStandsOut.push("Pay range and salary listed upfront");
  whyThisJobStandsOut.push("Full-time / regular structured schedule clarity");
  if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 5.0) {
    whyThisJobStandsOut.push("Within your selected local commute area");
  } else {
    whyThisJobStandsOut.push("Convenient nearby physical storefront or main office");
  }
  if (lead.rating !== undefined && lead.rating >= 4.4) {
    whyThisJobStandsOut.push(`Company has a strong local rating of ${lead.rating.toFixed(1)}★`);
  } else {
    whyThisJobStandsOut.push("Stable local employer with robust community standing");
  }
  if (lead.website) {
    whyThisJobStandsOut.push("Easy application link available directly online");
  } else {
    whyThisJobStandsOut.push("Direct voice/phone contact available for instant reach-out");
  }

  // Compile detailed explanations dictionary for the UI
  const tagExplanations: Record<string, TagExplanation> = {};
  const allTagsAndSignals = Array.from(new Set([
    ...finalWork,
    ...finalGrowth,
    ...finalService,
    ...finalContact,
    ...finalWarnings,
    ...finalAiAutomation,
    ...trustTags,
    ...convenienceTags,
    ...hiringTags,
    ...opportunityTags,
    ...sponsoredTags
  ])).filter(Boolean);
  
  for (const tag of allTagsAndSignals) {
    if (tag) {
      tagExplanations[tag] = getTagExplanation(tag, lead, distanceInMiles);
    }
  }

  // Generate Strengths, Opportunities to Improve, and Recommended Next Step
  const strengthsList: string[] = [];
  if (lead.rating !== undefined && lead.rating >= 4.4) {
    strengthsList.push(`Elite local rating of ${lead.rating.toFixed(1)}★`);
  } else if (lead.rating !== undefined && lead.rating >= 4.0) {
    strengthsList.push(`Strong customer feedback rating of ${lead.rating.toFixed(1)}★`);
  }
  if (lead.reviewsCount !== undefined && lead.reviewsCount >= 50) {
    strengthsList.push(`Robust customer reviews history (${lead.reviewsCount} reviews)`);
  } else if (lead.rating !== undefined && lead.reviewsCount !== undefined && lead.reviewsCount > 5) {
    strengthsList.push(`Active community reputation (${lead.reviewsCount} reviews)`);
  }
  if (lead.website) {
    strengthsList.push("Active business website and digital home");
  }
  if (lead.phone) {
    strengthsList.push("Direct voice/phone contact line listed");
  }
  const isOwnerOrManagerFound = positionsFound.some(r => r.includes("owner") || r.includes("founder") || r.includes("ceo") || r.includes("manager") || r.includes("director"));
  if (isOwnerOrManagerFound) {
    strengthsList.push("Verified key decision-maker contact details found");
  }
  if (isHighHiringIndustry) {
    strengthsList.push("High-demand local services industry segment");
  }
  if (strengthsList.length === 0) {
    strengthsList.push("Established local business ready for discovery and engagement");
  }

  const opportunitiesToImproveList: string[] = [];
  if (!lead.website) {
    opportunitiesToImproveList.push("Lacks a digital website (substantial online search visibility gap)");
  } else {
    if (lead.websiteAudit && !lead.websiteAudit.bookingButtonFound) {
      opportunitiesToImproveList.push("Lacks direct scheduling or online booking tools on their website");
    }
    if (lead.websiteAudit && !lead.websiteAudit.contactFormFound) {
      opportunitiesToImproveList.push("Missing an interactive lead capture or contact form on website");
    }
    if (lead.websiteAudit && (!lead.websiteAudit.mobileFriendly || !lead.websiteAudit.https)) {
      opportunitiesToImproveList.push("Website is outdated, lacking mobile responsiveness or HTTPS protection");
    }
  }
  if (!lead.phone) {
    opportunitiesToImproveList.push("Direct phone number is not listed publicly");
  }
  if (lead.reviewsCount === undefined || lead.reviewsCount < 10) {
    opportunitiesToImproveList.push("Low review count compared to established competitors in the area");
  }
  if (lead.rating !== undefined && lead.rating < 4.0) {
    opportunitiesToImproveList.push("Google rating average is below the 4.0 benchmark (reputation risk)");
  }
  if (opportunitiesToImproveList.length === 0) {
    opportunitiesToImproveList.push("No severe digital footprint gaps discovered; ready for premium promotion!");
  }

  // Determine the recommended next step
  let recommendedNextStepStr = "Draft a personalized outreach note to introduce your services and offer scheduling or consulting solutions.";
  if (!lead.website) {
    recommendedNextStepStr = "Create and launch a modern, mobile-friendly website with an integrated appointment scheduler to capture local search traffic.";
  } else if (lead.websiteAudit && !lead.websiteAudit.bookingButtonFound) {
    recommendedNextStepStr = "Integrate a modern calendar booking tool (like Calendly or custom scheduler) on their website to capture hot leads instantly.";
  } else if (lead.websiteAudit && !lead.websiteAudit.contactFormFound) {
    recommendedNextStepStr = "Add an interactive lead capture form on their homepage to convert after-hours web visitors.";
  } else if (lead.reviewsCount !== undefined && lead.reviewsCount < 15) {
    recommendedNextStepStr = "Launch a local review acquisition campaign to secure 10+ new positive reviews and boost Maps search ranking.";
  } else if (lead.rating !== undefined && lead.rating < 4.0) {
    recommendedNextStepStr = "Deploy a professional reputation repair sequence, requesting positive reviews from past satisfied clients to lift rating average.";
  }

  return {
    score,
    scoreBreakdown: breakItems,
    scoreConfidence,
    missingDataNotes,
    tags,
    pitchAngle,
    evidence,
    isAdult,
    categorizedSignals: {
      workOpportunities: finalWork,
      growthSignals: finalGrowth,
      serviceOpportunities: finalService,
      contactSignals: finalContact,
      warnings: finalWarnings,
      aiAutomation: finalAiAutomation,
    },
    tagExplanations,
    scores: {
      trustScore,
      trustBreakdown,
      convenienceScore,
      convenienceBreakdown,
      hiringScore,
      hiringBreakdown,
      opportunityScore,
      opportunityBreakdown,
      sponsoredScore,
      sponsoredBreakdown,
    },
    whyThisBusinessStandsOut,
    whyThisJobStandsOut,
    plainTags: {
      trust: trustTags,
      convenience: convenienceTags,
      hiring: hiringTags,
      opportunity: opportunityTags,
      sponsored: sponsoredTags,
    },
    strengths: strengthsList,
    opportunitiesToImprove: opportunitiesToImproveList,
    recommendedNextStep: recommendedNextStepStr,
  };
}

// Haversine distance calculation in miles
export function calculateHaversineDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 9999;
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Generates grounded, dynamic opportunity alerts with strict Haversine radius enforcement.
 * Zero out-of-bounds businesses allowed.
 */
export function generateGroundedOpportunityAlerts(
  userAlerts: any[],
  businesses: BusinessBusiness[],
  preferenceProfile?: any,
  trackingEnabled: boolean = true,
  userSearchLocation?: { lat: number; lng: number; city?: string }
): Array<{
  id: string;
  businessId: string;
  businessName: string;
  category: string;
  location: string;
  distanceMiles: number;
  type: "hiring" | "contact" | "job";
  title: string;
  description: string;
  time: string;
  email?: string;
  phone?: string;
  business: BusinessBusiness;
  score: number;
}> {
  if (!businesses || businesses.length === 0) return [];

  // Active location hierarchy determination
  let centerLat = userSearchLocation?.lat;
  let centerLng = userSearchLocation?.lng;

  if (!centerLat || !centerLng) {
    if (preferenceProfile?.preferredLocation?.lat && preferenceProfile?.preferredLocation?.lng) {
      centerLat = preferenceProfile.preferredLocation.lat;
      centerLng = preferenceProfile.preferredLocation.lng;
    }
  }

  // Fallback center from first valid business
  if (!centerLat || !centerLng) {
    const validBus = businesses.find(b => b.location?.lat && b.location?.lng);
    if (validBus) {
      centerLat = validBus.location.lat;
      centerLng = validBus.location.lng;
    } else {
      centerLat = 47.6062; // Default Seattle fallback
      centerLng = -122.3321;
    }
  }

  // Determine effective radius: default to 15 miles for alerts
  const alertRadius = userAlerts && userAlerts.length > 0 && userAlerts[0].max_distance
    ? Number(userAlerts[0].max_distance)
    : 15;

  const results: Array<{
    id: string;
    businessId: string;
    businessName: string;
    category: string;
    location: string;
    distanceMiles: number;
    type: "hiring" | "contact" | "job";
    title: string;
    description: string;
    time: string;
    email?: string;
    phone?: string;
    business: BusinessBusiness;
    score: number;
  }> = [];

  const seenIds = new Set<string>();

  businesses.forEach((business, index) => {
    if (!business || !business.name || seenIds.has(business.id)) return;

    // Check closed or blocked status
    if ((business.hiringStatus || "").toLowerCase() === "closed" || business.pipelineStage === "Blocked") {
      return;
    }

    const bLat = business.location?.lat;
    const bLng = business.location?.lng;

    let dist = 9999;
    if (bLat && bLng && centerLat && centerLng) {
      dist = calculateHaversineDistanceMiles(centerLat, centerLng, bLat, bLng);
    }

    // STRICT DISTANCE FILTER: Business OUTSIDE radius is strictly excluded
    if (dist > alertRadius) {
      return;
    }

    seenIds.add(business.id);

    const prospect = analyzeBusinessProspect(business, dist);
    
    // Determine alert type based on signals
    let type: "hiring" | "contact" | "job" = "hiring";
    let title = "Active Hiring Signal Detected";
    let description = `${business.name} shows high local demand and recent activity indicating capacity expansion or staffing needs.`;

    const isHiring = (prospect.categorizedSignals?.workOpportunities || []).length > 0 || prospect.scores?.hiringScore >= 70;
    const hasDirectContact = business.contacts && business.contacts.length > 0 && business.contacts.some(c => c.email);

    if (hasDirectContact) {
      type = "contact";
      title = "Verified Decision-Maker Endpoint Found";
      description = `Direct contact information verified on file for ${business.name}.`;
    } else if (isHiring) {
      type = "hiring";
      title = "Immediate Local Hiring Opportunity";
      description = `Recent growth signals suggest unadvertised hiring for support and operational roles in ${business.category || "local trades"}.`;
    } else {
      type = "job";
      title = "New Matching Employer Nearby";
      description = `Employer in ${business.category || "your area"} matches your preferred commute distance (${dist.toFixed(1)} miles).`;
    }

    const primaryEmail = business.contacts?.find(c => c.email)?.email;

    results.push({
      id: `alt-${business.id}`,
      businessId: business.id,
      businessName: business.name,
      category: business.category || "Local Business",
      location: business.address || "Local Area",
      distanceMiles: Math.round(dist * 10) / 10,
      type,
      title,
      description,
      time: index === 0 ? "10 mins ago" : index === 1 ? "2 hours ago" : "Today",
      email: primaryEmail,
      phone: business.phone,
      business,
      score: prospect.score
    });
  });

  // Sort by distance ascending, then prospect score descending
  return results.sort((a, b) => a.distanceMiles - b.distanceMiles || b.score - a.score);
}

